import { useReducer, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Lfa_Service_Acceptance_Status,
  type Date__Input,
  type Lfa_Service_Feedback,
} from '~/graphql/types';
import reducer, {
  createInitialState,
  type State,
  type UpdateFeedbackArgs,
} from '../../utils/reducer';
import buildFeedback from '../../utils/buildFeedback';
import useErrorReporter from '~/hooks/useErrorReporter';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import useFeedbackValidation, {
  type ClearValidationErrorArgs,
} from '~/components/page/LFA/hooks/useFeedbackValidation';
import type { ServicesFeedbackType } from '~/components/page/LFA/state/servicesFeedbackState';
import type {
  ValidationError,
  ValidationField,
} from '~/components/page/LFA/state/validationState';
import type {
  ClientLfaConversion,
  ClientLfaConversionData,
} from '~/components/page/LFA/types';

const text = {
  errors: {
    serviceIdRequired: 'Service ID is required',
    feedbackUpdateFailed: 'Feedback kon niet worden bijgewerkt',
    serviceAddFailed: 'Service kon niet worden toegevoegd',
    feedbackProcessingFailed: 'Feedback kon niet worden verwerkt',
  },
};

export type UseServiceFeedbackType = {
  state: State;
  errorsToShowLookup: Partial<Record<ValidationField, Array<ValidationError>>>;
  hasAdditionalServices: boolean;
  additionalServices: ClientLfaConversionData['enabledServices'];
  hasSnoozedService: boolean;
  clearValidationErrorForField: (args: ClearValidationErrorArgs) => void;
  buildServicesFeedback: () => ServicesFeedbackType;
  handleUpdateFeedback: (args: UpdateFeedbackArgs) => void;
  handleRemoveService: (
    service: ClientLfaConversion['selectedServices'][number],
  ) => void;
  handleToggleAdditionalServices: () => void;
  handleSnoozeUntilChange: (date: Date__Input) => void;
  handleAddService: (
    service: ClientLfaConversionData['enabledServices'][number],
  ) => void;
  handleSnoozeNoteChange: (note: string) => void;
  handleAdditionalCommentsChange: (comments: string) => void;
};

type Args = {
  initialSelectedServices: ClientLfaConversion['selectedServices'];
  lastServiceFeedback?: Array<Lfa_Service_Feedback> | null;
  enabledServices: ClientLfaConversionData['enabledServices'];
};

const useServiceFeedback = ({
  initialSelectedServices,
  lastServiceFeedback,
  enabledServices,
}: Args): UseServiceFeedbackType => {
  const errorReporter = useErrorReporter();
  const addToast = useAddToast();

  // Use ref to track if we've set the initial feedback
  const initializedRef = useRef(false);
  const [state, dispatch] = useReducer(
    reducer,
    initialSelectedServices,
    createInitialState,
  );
  const { errorsToShowLookup, clearValidationErrorForField } =
    useFeedbackValidation({
      validationFor: ['Done', 'Snooze'],
    });

  const additionalServices = useMemo(
    () =>
      enabledServices.filter(
        service => !state.selectedServices.some(s => s.id === service.id),
      ),
    [enabledServices, state.selectedServices],
  );

  const snoozedService = useMemo(
    () =>
      Array.from(state.serviceFeedback.values()).find(
        feedback => feedback.answer === Lfa_Service_Acceptance_Status.Snoozed,
      ),
    [state.serviceFeedback],
  );

  const hasSnoozedService = Boolean(snoozedService);
  const hasAdditionalServices = additionalServices.length > 0;

  const handleUpdateFeedback = useCallback(
    (args: UpdateFeedbackArgs) => {
      try {
        dispatch({ type: 'UPDATE_SERVICE_FEEDBACK', payload: args });
      } catch (error) {
        errorReporter.captureException(error as Error, 'error');
        addToast([
          formatToastMessage(text.errors.feedbackUpdateFailed, 'danger'),
        ]);
      }
    },
    [errorReporter, addToast],
  );

  const handleAddService = useCallback(
    (service: ClientLfaConversionData['enabledServices'][number]) => {
      dispatch({
        type: 'ADD_SERVICE',
        payload: { service: service, lastFeedback: lastServiceFeedback },
      });
    },
    [lastServiceFeedback],
  );

  const handleToggleAdditionalServices = useCallback(() => {
    dispatch({ type: 'TOGGLE_ADDITIONAL_SERVICES' });
  }, []);

  const handleSnoozeUntilChange = (date: Date__Input) => {
    clearValidationErrorForField({ key: 'Snooze', field: 'snoozeDate' });
    dispatch({ type: 'SET_SNOOZE_UNTIL', payload: { date } });
  };

  const handleSnoozeNoteChange = useCallback((note: string) => {
    clearValidationErrorForField({ key: 'Snooze', field: 'snoozeNote' });
    dispatch({ type: 'SET_SNOOZE_NOTE', payload: { note } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdditionalCommentsChange = useCallback((comments: string) => {
    dispatch({ type: 'SET_ADDITIONAL_COMMENTS', payload: { comments } });
  }, []);

  const handleRemoveService = useCallback(
    (service: ClientLfaConversion['selectedServices'][number]) => {
      dispatch({ type: 'REMOVE_SERVICE', payload: { service } });
    },
    [],
  );

  const buildServicesFeedback = useCallback(
    () =>
      buildFeedback({
        state,
        hasSnoozedService,
        errorReporter,
        addToast,
        errorMessage: text.errors.feedbackProcessingFailed,
      }),
    [state, hasSnoozedService, errorReporter, addToast],
  );

  useEffect(() => {
    if (!state.initialized && !initializedRef.current) {
      initializedRef.current = true;
      dispatch({
        type: 'INITIALIZE_FEEDBACK',
        payload: {
          services: initialSelectedServices,
          lastFeedback: lastServiceFeedback,
        },
      });
    }
  }, [initialSelectedServices, lastServiceFeedback, state.initialized]);

  return {
    state,
    hasSnoozedService,
    additionalServices,
    clearValidationErrorForField,
    hasAdditionalServices,
    handleUpdateFeedback,
    handleRemoveService,
    handleToggleAdditionalServices,
    buildServicesFeedback,
    handleAddService,
    handleSnoozeUntilChange,
    handleSnoozeNoteChange,
    errorsToShowLookup,
    handleAdditionalCommentsChange,
  };
};

export default useServiceFeedback;
